'use client'

import Container from '@/components/templates/container'
import CoachingPage from '@/components/organisms/sportPage/coachingPage'
import { Photo, body, h2 } from '@/components/organisms/sportPage'

const WHATSAPP = 'https://wa.me/6281337582672'

const FALLBACK_COACH = {
  name: 'Personal Trainer',
  position: 'Head Personal Trainer',
  bio: 'Our personal trainer works with members and guests in Mahima\'s high-performance gym. Whether your goal is to build strength, improve athletic performance, or recover smarter, sessions are fully tailored to your individual needs and schedule.',
  bio2: 'Training is available as standalone sessions or as part of a broader program integrated with court coaching and recovery services — sauna, ice bath, and spa treatments — all under one roof.',
  focusAreas: [
    { label: 'Strength and conditioning' },
    { label: 'Athletic performance' },
    { label: 'Weight loss and body composition' },
    { label: 'Mobility and flexibility' },
    { label: 'Recovery and injury prevention' },
    { label: 'Personalised programming' },
  ],
}

const FALLBACK_TRAINING = [
  'One-on-one personal coaching',
  'Strength and conditioning',
  'Athletic performance training',
  'Weight loss programs',
  'Functional fitness',
  'Mobility and flexibility training',
  'Recovery and injury prevention',
  'Beginner-friendly fitness guidance',
]

const FALLBACK_RECOVERY = [
  'Sauna',
  'Ice baths',
  'Dedicated recovery spaces',
  'Healthy food and drinks on-site',
  'Wellness-focused lifestyle facilities',
]

const FALLBACK_SESSIONS = [
  {
    title: 'Personal Training',
    description: 'One-on-one sessions built entirely around your goals — strength, athletic performance, weight loss, or simply feeling better. Fully personalised programming.',
    price: '',
    duration: '60 min',
    bookingUrl: 'https://wa.me/6281337582672',
    bookingLabel: 'Book via WhatsApp',
  },
  {
    title: 'Small Group Session',
    description: 'Train alongside others in a motivated, coach-led environment. Small group sizes keep the training quality high and the energy even higher.',
    price: '',
    duration: '60 min',
    bookingUrl: 'https://wa.me/6281337582672',
    bookingLabel: 'Book via WhatsApp',
  },
  {
    title: 'Performance Program',
    description: 'A structured multi-week training program for athletes wanting to take their conditioning to the next level. Ideal for padel and tennis players.',
    price: '',
    duration: 'Program',
    bookingUrl: 'https://wa.me/6281337582672',
    bookingLabel: 'Book via WhatsApp',
  },
]

const DEFAULT_INTROS = [
  "Train smarter and recover better at Mahima Bali's high-performance gym in Sayan, Ubud. Our personal trainers create fully customised programs for every goal (strength, fat loss, athletic performance or general wellbeing) supported by modern equipment, expert coaching and recovery facilities all under one roof.",
  'Mahima combines fitness, sport, recovery and community into one complete lifestyle destination in Bali. More than a gym: a place to train, recharge and grow.',
]

// Training and recovery: two lined lists, each with a photo
const ServicePanel = ({ heading, items, photo }) => (
  <div className="flex flex-col gap-6">
    <Photo image={photo} alt={`${heading} at Mahima, Sayan, Ubud`} className="aspect-[4/3]" />
    <h3 className="font-ttDrugsTrial text-olive text-[26px] md:text-[32px] leading-none">{heading}</h3>
    <ul className="border-b border-dune">
      {items.map((item) => (
        <li key={item} className="font-chivoMono text-[13px] text-olive border-t border-dune py-2.5">{item}</li>
      ))}
    </ul>
  </div>
)

const GymCoachingPage = ({ coachingAPI, footerAPI, settingAPI }) => {
  const hero = coachingAPI?.hero || {}
  const cta = coachingAPI?.cta || {}
  const coach = coachingAPI?.coach?.name ? coachingAPI.coach : FALLBACK_COACH
  const training = coachingAPI?.trainingServices?.length ? coachingAPI.trainingServices.map((s) => s.label) : FALLBACK_TRAINING
  const recovery = coachingAPI?.recoveryServices?.length ? coachingAPI.recoveryServices.map((s) => s.label) : FALLBACK_RECOVERY
  const sessions = (coachingAPI?.sessions?.length ? coachingAPI.sessions : FALLBACK_SESSIONS).map((s) => ({
    ...s,
    url: s.bookingUrl || WHATSAPP,
    urlLabel: s.bookingLabel || 'Book',
  }))
  const bookUrl = cta.bookingUrl || WHATSAPP
  const intros = coachingAPI ? [hero.intro, hero.intro2] : DEFAULT_INTROS

  return (
    <CoachingPage
      footerAPI={footerAPI}
      settingAPI={settingAPI}
      eyebrow={hero.label || 'Personal training in Sayan, Ubud'}
      heading={hero.heading || 'Personal Training & Gym at Mahima'}
      intros={intros}
      classesHeading={coachingAPI?.sessionsHeading || 'Sessions & Programs'}
      classes={sessions}
      classesImage={coachingAPI?.sessionsImage}
      classesImageAlt="Personal training at Mahima, Sayan, Ubud"
      coaches={[{ ...coach, focusAreas: (coach.focusAreas || []).map((f) => f.label).filter(Boolean) }]}
      focusLabel="Training focuses on"
      bookUrl={bookUrl}
      bookLabel={cta.bookingLabel || 'Book a Session'}
      cta={{
        heading: cta.heading || 'Train With Purpose.',
        description: cta.description || "Book a personal training session at Mahima's high-performance gym in Sayan, Ubud.",
        url: bookUrl,
        label: cta.bookingLabel || 'Book a Session',
      }}
    >
      <Container outerClassName="mt-[100px] lg:mt-[150px]">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 lg:gap-20 mb-10 lg:mb-12">
          <h2 className={h2}>Training &amp; Recovery</h2>
          {coachingAPI?.servicesIntro && <p className={`${body} max-w-[560px]`}>{coachingAPI.servicesIntro}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-6">
          <ServicePanel heading="Training" items={training} photo={coachingAPI?.trainingPhoto} />
          <ServicePanel heading="Recovery" items={recovery} photo={coachingAPI?.recoveryPhoto} />
        </div>
      </Container>
    </CoachingPage>
  )
}

export default GymCoachingPage
